import {isFeatureEnabled} from 'feature-flags';
import {Card, DataStatus, EmptyStatus, Separator} from 'platform/components';
import {Grid, GridItem, Hide, Show, Space, VStack} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  CommentsWidget,
  EntityCustomFields,
  Main,
  NoteWidget,
  usePermissions,
  usePricePermissions,
} from '@dms/shared';

import {isNotNilOrEmpty, useRequiredParams} from 'shared/web';

import {BusinessCaseTradeInCounterpartCard} from '../../components/BusinessCaseTradeIn/components/BusinessCaseTradeInCounterpartCard';
import {useBusinessCase} from '../../hooks/useBusinessCase';
import {isBrokerageVehicle} from '../../utils/isBrokerageVehicle';
import {ClosedBusinessCaseDetails} from './components/ClosedBusinessCaseDetails';
import {CustomerSatisfactionCard} from './components/CustomerSatisfactionCard/CustomerSatisfactionCard';
import {FinancialOverview} from './components/FinancialOverview';
import {VehicleOverview} from './components/VehicleOverview';

export function BusinessCaseOverview() {
  const {id} = useRequiredParams();

  const {
    id: businessCaseId,
    businessCase: {interestId: _interestId, ...businessCase},
    linkedTradeInBusinessCaseId,
    state,
    type,
    isLoading: isLoadingBusinessCase,
    isError: hasBusinessCaseError,
    saleVehicles,
    purchaseVehicles,
  } = useBusinessCase(id);

  const {canViewAnyOfSalePriceFields, canViewAnyOfPurchasePriceFields} = usePricePermissions({
    businessCaseRecordId: businessCaseId,
    vehicleRecordId: null,
  });

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      recordId: businessCaseId ?? '',
      resourceId: EntityResourceIds.businessCase,
    },
    {
      skip: isNilOrEmpty(businessCaseId),
    }
  );

  const [
    canSetBusinessCasePurchaseNote,
    canReadBusinessCaseComments,
    canAddBusinessCaseComments,
    canDeleteBusinessCaseComments,
    canEditCustomFields,
  ] = usePermissions({
    permissionKeys: [
      'setBusinessCasePurchaseNote',
      'businessCaseCommentRead',
      'businessCaseCommentAdd',
      'businessCaseCommentDelete',
      'businessCaseCustomFields',
    ],
    scopes: {
      setBusinessCasePurchaseNote: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      businessCaseCommentRead: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      businessCaseCommentAdd: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      businessCaseCommentDelete: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      businessCaseCustomFields: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
    },
  });
  const isNoteEditable =
    businessCase?.businessCaseInternalType === 'SELLING' ? true : canSetBusinessCasePurchaseNote;

  const isTradeInFlagEnabled = isFeatureEnabled(featureFlags.TRADE_IN);
  const isClosedSale = state === 'CLOSED' && (type === 'SELLING' || type === 'SWAP');

  return (
    <Main>
      <DataStatus isLoading={isLoadingBusinessCase} isError={hasBusinessCaseError}>
        <Grid columns={[1, 2]} align="flex-start">
          <Show when={isClosedSale}>
            <GridItem span={[1, 2]}>
              <CustomerSatisfactionCard
                businessCaseId={businessCaseId!}
                data-testid={testIds.businessCase.overview('customerSatisfaction')}
              />
            </GridItem>
          </Show>
          <Show when={state === 'UNSUCCESSFUL'}>
            <GridItem span={[1, 2]}>
              <ClosedBusinessCaseDetails
                businessCase={businessCase}
                data-testid={testIds.businessCase.overview('closedBusinessCaseDetails')}
              />
            </GridItem>
          </Show>
          <Show when={canViewAnyOfSalePriceFields || canViewAnyOfPurchasePriceFields}>
            <GridItem span={[1, 2]}>
              <FinancialOverview businessCaseId={businessCaseId!} />
            </GridItem>
          </Show>
          <GridItem span={[1, 2]}>
            <Card
              title={i18n.t('entity.vehicle.labels.pageTitle')}
              data-testid={testIds.businessCase.overview('vehicles')}
            >
              <Show when={Boolean(saleVehicles?.length) || Boolean(purchaseVehicles?.length)}>
                <VStack>
                  {saleVehicles?.map((vehicle, i) => (
                    <VehicleOverview
                      data-testid={testIds.businessCase.overview(`saleVehicle[${i}]`)}
                      key={vehicle.id}
                      vehicleId={vehicle.vehicleId}
                      businessCaseId={id}
                    />
                  ))}
                  <Show when={isNotNilOrEmpty(purchaseVehicles) && isNotNilOrEmpty(saleVehicles)}>
                    <Separator />
                  </Show>
                  {purchaseVehicles?.map((vehicle, i) => (
                    <VehicleOverview
                      key={vehicle.id}
                      data-testid={testIds.businessCase.overview(`purchaseVehicle[${i}]`)}
                      vehicleId={vehicle.vehicleId}
                      businessCaseId={id}
                      isBrokerageOfferVehicle={
                        isBrokerageVehicle(vehicle) ||
                        ('purchaseVehicleType' in vehicle &&
                          vehicle.purchaseVehicleType === 'BROKERAGE')
                      }
                    />
                  ))}
                </VStack>
              </Show>
              <Hide when={Boolean(saleVehicles?.length) || Boolean(purchaseVehicles?.length)}>
                <EmptyStatus
                  data-testid={testIds.businessCase.overview('no-vehicles')}
                  headline={i18n.t('entity.vehicle.notifications.noVehicles')}
                  subheadline={i18n.t('entity.vehicle.notifications.noVehiclesSub')}
                />
              </Hide>
            </Card>
          </GridItem>
          {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
          {isTradeInFlagEnabled && isNotNilOrEmpty(linkedTradeInBusinessCaseId) && (
            <GridItem span={[1, 2]}>
              <BusinessCaseTradeInCounterpartCard
                linkedTradeInBusinessCaseId={linkedTradeInBusinessCaseId}
                hostCustomerId={businessCase?.customerId}
                data-testid={testIds.businessCase.overview('tradeInCounterpart')}
              />
            </GridItem>
          )}
          <GridItem>
            <Show when={canReadBusinessCaseComments}>
              <VStack spacing={4}>
                <EntityCustomFields
                  recordId={id}
                  resourceId={EntityResourceIds.businessCase}
                  isEditable={canEditCustomFields}
                  data-testid={testIds.vehicles.detail('vehicle-custom-fields')}
                />
                <CommentsWidget
                  data-testid={testIds.businessCase.overview('comments')}
                  title={i18n.t('entity.businessCase.labels.businessCaseComments')}
                  resourceId={EntityResourceIds.businessCase}
                  recordId={businessCaseId ?? ''}
                  isAddDisabled={!canAddBusinessCaseComments}
                  isDeleteDisabled={!canDeleteBusinessCaseComments}
                />
              </VStack>
            </Show>
            <Hide when={canReadBusinessCaseComments}>
              <Space fillAvailable />
            </Hide>
          </GridItem>
          <GridItem>
            <NoteWidget
              data-testid={testIds.businessCase.overview('note')}
              resourceId={EntityResourceIds.businessCase}
              recordId={businessCaseId ?? ''}
              context={EntityResourceIds.businessCase}
              isEditable={isNoteEditable}
            />
          </GridItem>
        </Grid>
      </DataStatus>
    </Main>
  );
}
